import React, { createContext, useEffect, useState, useCallback } from "react";
import { getMyProfileAPI } from "../../utils/Api";

export const StudentContext = createContext();

export const StudentProvider = ({ children }) => {
  const [student, setStudent] = useState(() => {
    const savedStudent = localStorage.getItem("student");
    return savedStudent ? JSON.parse(savedStudent) : null;
  });
  // lifecycleStatus + portalAccess, read from the server rather than trusted from login.
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // localStorage holds a snapshot written once at login and never refreshed, so a student
  // whose access changed mid-session kept seeing menu items the server would refuse. Re-read
  // the profile on mount so the navigation reflects what they can actually do now.
  const refreshProfile = useCallback(async () => {
    if (!localStorage.getItem("studentToken")) {
      setProfile(null);
      return null;
    }
    setProfileLoading(true);
    try {
      const res = await getMyProfileAPI();
      setProfile(res?.data || null);
      return res?.data || null;
    } catch (err) {
      // 401/403 means the session is no longer valid; the route guards handle that. Don't
      // trap the student on a broken page here.
      setProfile(null);
      return null;
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  useEffect(() => {
    if (student) {
      localStorage.setItem("student", JSON.stringify(student));
    } else {
      localStorage.removeItem("student");
    }
  }, [student]);

  // No profile (still loading, offline, older backend) => allow. This only hides links; the
  // server is the real gate, so failing open here cannot grant access, it just avoids
  // blanking the menu on a transient error.
  const can = useCallback(
    (flag) => {
      if (!profile?.portalAccess) return true;
      return !!profile.portalAccess[flag];
    },
    [profile],
  );

  const lifecycleStatus = profile?.lifecycleStatus || null;
  const isCurrentlyStudying =
    !lifecycleStatus || ["active", "detained"].includes(lifecycleStatus);

  return (
    <StudentContext.Provider
      value={{
        student,
        setStudent,
        profile,
        profileLoading,
        refreshProfile,
        can,
        lifecycleStatus,
        isCurrentlyStudying,
      }}
    >
      {children}
    </StudentContext.Provider>
  );
};
